import { useState, useEffect, useRef } from "react";
import { Button } from "../../Components/UI/button";
import { Input } from "../../Components/UI/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from "../../Components/UI/table";
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from "../../Components/UI/tooltip";
import { Search, Filter, ArrowUp, ArrowDown, CalendarIcon, ArrowDownWideNarrow, UserCircle, Pencil, Building2, FileText, Paperclip, HourglassIcon, BookUser, NotebookTabs, Recycle, ArrowLeft } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardContent } from "../../Components/UI/card";
import PaginationComp from "../../Components/Common/Pagination";
import {
    currentUserHasPermission,
    FormatAmount, getColorClass, getContractIcon, getCurrencySymbol, getDateRangeOptions, getNestedValue, getOtherYearOptions, getPastMonthOptions, getPastQuarterOptions, getTaskColor,
} from "../../Utils/Utilities";
import ContactActions from "../../Components/Common/ContactActions";
import { CommonFilter } from "../../Components/Common/CommonFilter";
import { cn } from "../../Lib/Cn";
import { format } from 'date-fns';
import { Drawer, DrawerClose, DrawerContent, DrawerFooter } from "../../Components/UI/drawer";
import DrawerFilter from "../../Components/Common/DrawerFilter";
import { RiResetLeftFill } from "react-icons/ri";
import useIsMobile from "../../Hooks/useIsMobile";
import { useVirtualizer } from "@tanstack/react-virtual";
import { setGlobalLoading } from "../../Store/Actions";
import { toast } from "sonner";
import ContractEntry from "./ContractEntry";
import { getContractAPI } from "./ContractsAPI";
import ContractDropdown from "../Connections/ContractDropdown";
import { BsViewStacked } from "react-icons/bs";
import { useCurrency } from "../../Hooks/useCurrency";
import { getTableDateOrWeekday } from "../../Utils/DateTime";
import ContractDetails from "../Connections/Tabs/ContractDetails";
import { useConnectionFilter } from '../../Hooks/useConnectionFilter';

const sortKeyMap = {
    bizName: "bizName",
    contact: "contactName"
};

const sortkeyMapMobileFilter = {
    "Amount (highest first)": { key: "contractCharges", direction: "desc" },
    "Amount (lowest first)": { key: "contractCharges", direction: "asc" },
    "Started (newest first)": { key: "startDT", direction: "desc" },
    "Earliest Expiry": { key: "expiryDT", direction: "asc" },
    "Business name (A-Z)": { key: "bizName", direction: "asc" },
}

//Local CSS Classes
var filterBtn = "button-config sahi-primary-button flex items-center justify-center group px-2.5 py-[5px]";
var actionButton = "action-btn-effect hover:bg-sahi-blue-light";

const Contracts = () => {
    const location = useLocation();
    const isMobile = useIsMobile();
    const clickedTabRef = useRef(null);
    const parentRef = useRef(null);
    const dispatch = useDispatch()
    const searchParams = new URLSearchParams(location.search);
    const [paramConnID, setParamConnID] = useState('')
    const currencySymbol = useCurrency()
    const { data: connectionData } = useSelector((state) => state.connectionData);

    // Apply connection filtering based on user permissions
    const { filteredConnections: permissionFilteredConnections } = useConnectionFilter(connectionData, {
        connectionTypeFilter: null,
        includeAddOption: false
    });
    const [isLoading, setIsLoading] = useState(true);
    const contractTypeOptions = useSelector(state => {
        const contractTypes = state.masterData.contractTypes || [];
        return contractTypes.map(type => ({
            value: type.Typ || type.TYP || type.name,
            ID: type.CID
        }));
    })
    const [ctrc, setCtrc] = useState([]);
    const [filteredCtrc, setFilteredCtrc] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeTab, setActiveTab] = useState("sort");
    const [preSearchCtrc, setPreSearchCtrc] = useState([]); // filtered by filters only
    const prevFiltersRef = useRef({});
    const [hideTypeColumn, setHideTypeColumn] = useState(false);
    const [connData, setConndata] = useState([])
    const [currentCtrc, setCurrentCtrc] = useState([]);
    const perm = currentUserHasPermission('CtrcActions');
    const canEditContracts = perm === 1 || perm === 2;
    const canAddContracts = perm === 1 || perm === 3;

    const [selectedContract, setSelectedContract] = useState(null);
    const [contractPrefilledData, setContractPrefilledData] = useState({});
    const [contractDropdownOpen, setContractDropdownOpen] = useState(false);
    const [contractDropdownOpen2, setContractDropdownOpen2] = useState(false);
    // useful for sorting and filter
    const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
    const [isFilterSelected, setIsFilterSelected] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState({
        connID: "all",
        startDT: "all",
        expiryDT: isMobile ? "all" : "This Month",
        status: 1,
        cType: "all",
        ctrcType: "all",
        dateRange: isMobile ? "all" : "This Month",
        otherMonth: "all",
        otherQuarter: "all",
        otherYear: "all",
        instance: "all",
    });
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState(selectedFilters);
    const [tempsortConfig, setTempsortConfig] = useState(sortConfig);
    const [dialogStates, setDialogStates] = useState({
        contractEntry: false,
        contractDetails: false,
    })

    const [selectedContractForDetail, setSelectedContractForDetail] = useState(null);
    const typeOptions = [{ name: 'New', ID: 'new' }, { name: 'Renewal', ID: 'renewal' }]

    const handleContractTypeSelection = (contractTypeID) => {
        if (contractTypeID === 'sold') {
            const connDataObj = connData.find(c => c.connID.toString() === paramConnID.toString())
            setSelectedContract(null);
            setContractPrefilledData({
                contractTypeSelection: 'sold',
                connection: paramConnID !== '' ? paramConnID : null,
                bizName: paramConnID !== '' ? connDataObj?.bizName : "",
                contactName: paramConnID !== '' ? connDataObj?.contactName : ""
            });
            setDialogStates((prev) => ({ ...prev, contractEntry: true }));
        } else if (contractTypeID === 'purchase') {
            // Supplier contract
            const connDataObj = connData.find(c => c.connID.toString() === paramConnID.toString())
            setSelectedContract(null);
            setContractPrefilledData({
                contractTypeSelection: 'purchase',
                connection: paramConnID !== '' ? paramConnID : null,
                bizName: paramConnID !== '' ? connDataObj?.bizName : "",
                contactName: paramConnID !== '' ? connDataObj?.contactName : ""
            });
            setDialogStates((prev) => ({ ...prev, contractEntry: true }));
        }
        setContractDropdownOpen(false);
    };

    useEffect(() => {
        if (!isMobile) {
            setIsFilterSelected(true)
        }
    }, [])

    const tableHeaderCols = () => {
        const baseColumns = [];

        // Only add bizType column if it shouldn't be hidden
        if (!hideTypeColumn) {
            baseColumns.push({ key: "bizType", label: "", className: "align-middle text-text-color w-[3vw] " });
        }

        const conditionalColumns = [];

        if (selectedFilters.connID === 'all') {
            conditionalColumns.push({ key: 'bizName', label: 'Business', className: 'align-middle' });
        }

        conditionalColumns.push({ key: 'contractNo', label: 'Ref No.', className: 'align-middle text-text-color' });

        if (selectedFilters.ctrcType === 'all') {
            conditionalColumns.push({ key: 'contractType', label: 'Type', className: 'align-middle text-text-color' });
        }

        conditionalColumns.push({ key: "instance", label: "Instance", className: "align-middle" });

        conditionalColumns.push({ key: "product", label: "Offering", className: "align-middle text-text-color" });

        conditionalColumns.push({
            key: "contractCharges",
            label: (
                <div className="flex items-center justify-end w-full">
                    Charges&nbsp;
                    <span className="text-base mx-0.5">
                        (<span>{currencySymbol}</span>)
                    </span>
                </div>
            ),
            className: "text-right"
        });

        conditionalColumns.push({ key: "startDT", label: "Start", className: "align-middle" });

        conditionalColumns.push({
            key: "renewalCharges",
            label: <div className="flex items-center justify-end w-full">
                Renewal&nbsp;
                <span className="text-base mx-0.5">
                    (<span>{currencySymbol}</span>)
                </span>
            </div>,
            className: "align-middle text-right"
        });

        conditionalColumns.push({ key: "expiryDT", label: "Expiry", className: "align-middle" });
        conditionalColumns.push({ key: "status", label: "Status", className: "align-middle" });

        // Return base columns first, then conditional columns
        return [
            ...baseColumns,
            ...conditionalColumns
        ];
    };

    // Helper functions for conditional UI
    const getActiveContracts = (contracts) => {
        return contracts.filter(ctrc => {
            const hasExpired = ctrc.expiryDT && new Date(ctrc.expiryDT) < new Date();
            const isActive = !ctrc.cancelledDT && !hasExpired;
            return isActive;
        });
    };

    const hasActiveData = (contracts) => {
        return getActiveContracts(contracts).length > 0;
    };

    const shouldShowFullUI = (contracts, selectedFilters) => {
        // Always show full UI if we have active data, regardless of current filter results
        if (hasActiveData(contracts)) {
            return true;
        }

        // If no active data but user is viewing inactive contracts, show full UI
        if (selectedFilters.status === 2 && contracts.length > 0) {
            return true;
        }

        return false;
    };

    const getFilterOptions = (showFullFilters, contractTypeOptions, connData, selectedFilters, tempFilters) => {
        // Base filter - always show Active/Inactive status
        const statusOption = [
            { name: "Renewal Month basis", ID: 1 },
            { name: "Contract Start basis", ID: 2 },
        ]
        // Full filter options when we have data to show

        const baseFilter = {
            key: "status",
            label: "All Status",
            options: statusOption,
            type: "options",
            displayValue: (val) => {
                return statusOption.find((c) => c.ID === val)?.name || "";
            },
            mobileDropdownText: "Select Status",
            searchEnabled: false,
            showClearButton: false,
            hideAllOption: true,
            MobileLabel: "Status"
        };
        // If we shouldn't show full filters, return only the status filter
        // if (!showFullFilters) {
        //     return [baseFilter];
        // }

        // Full filter options when we have data to show
        const isActive = (tempFilters?.status || selectedFilters.status) === 1;
        return [
            baseFilter,
            {
                key: "dateRange",
                label: "All Time",
                options: getDateRangeOptions(),
                searchEnabled: false,
                type: "options",
                disabled: isActive,
                mobiledisabled: (tempFilters?.status || selectedFilters.status) === 1,
                MobileLabel: "Time",
                showAllOption: true,
                showClearButton: false

            },
            {
                key: "otherMonth",
                label: "Select Month",
                options: getPastMonthOptions(),
                disabled: !(!isActive && (selectedFilters.dateRange === "Other Month" || (tempFilters?.dateRange || selectedFilters.dateRange) === "Other Month")),
                mobiledisabled: !((tempFilters?.status || selectedFilters.status) !== 1 && (tempFilters?.dateRange || selectedFilters.dateRange) === "Other Month"),
                type: "list",
                mobileDropdownText: "Select Month",
                mobileNoLabel: true,
                isSorting: false
            },
            {
                key: "otherQuarter",
                label: "Select Quarter",
                options: getPastQuarterOptions(),
                disabled: !(!isActive && (selectedFilters.dateRange === "Other Quarter" || (tempFilters?.dateRange || selectedFilters.dateRange) === "Other Quarter")),
                mobiledisabled: !((tempFilters?.status || selectedFilters.status) !== 1 && (tempFilters?.dateRange || selectedFilters.dateRange) === "Other Quarter"),
                type: "list",
                mobileDropdownText: "Select Quarter",
                mobileNoLabel: true,
                isSorting: false
            },
            {
                key: "otherYear",
                label: "Select Year",
                options: getOtherYearOptions(),
                disabled: !(!isActive && (selectedFilters.dateRange === "Other Year" || (tempFilters?.dateRange || selectedFilters.dateRange) === "Other Year")),
                mobiledisabled: !((tempFilters?.status || selectedFilters.status) !== 1 && (tempFilters?.dateRange || selectedFilters.dateRange) === "Other Year"),
                type: "list",
                mobileDropdownText: "Select Year",
                mobileNoLabel: true,
                isSorting: false
            },
            {
                key: "expiryDT",
                label: "All Time",
                options: ["This Month", "Next Month", "This Quarter", "This Year"],
                type: "options",
                searchEnabled: false,
                disabled: !isActive,
                mobiledisabled: !((tempFilters?.status || selectedFilters.status) === 1),
                MobileLabel: "Renewal",
                showAllOption: true,
                showClearButton: false
            },
            {
                key: "cType",
                label: "All Types",
                options: ["Customer", "Supplier"],
                type: "options",
                mobileDropdownText: "Select Type",
                searchEnabled: false,
                MobileLabel: "Contact Type",
                showAllOption: true
            },
            {
                key: "ctrcType",
                label: "All Contracts",
                options: contractTypeOptions.map((c) => ({ name: c.value, ID: c.ID })),
                type: "options",
                displayValue: (val) => {
                    const numVal = Number(val);
                    return contractTypeOptions.find((c) => Number(c.ID) === numVal)?.value || "";
                },
                mobileDropdownText: "Select Type",
                searchEnabled: false,
                MobileLabel: "Type",
                showAllOption: true
            },
            {
                key: "connID",
                label: "All Contacts",
                options: connData.map((c) => ({ name: c.bizName || c.contactName, ID: c.connID })),
                displayValue: (val) => { 
                    let displayText = '';
                    let bizName = connData.find(c => Number(c.connID) === Number(val))?.bizName
                    if(bizName == '')
                    {
                        displayText = connData.find(c => Number(c.connID) === Number(val))?.contactName || ''
                    }
                    else{
                        displayText = bizName;
                    }
                    return displayText;
                },
                type: "list",
                mobileDropdownText: "Select Contact",
                MobileLabel: "Contact",
                showAllOption: true
            },
            {
                key: "instance",
                label: "All Instances",
                options: typeOptions,
                displayValue: (val) => {
                    return typeOptions.find((t) => t.ID === val)?.name || ""
                },
                type: "options",
                mobileDropdownText: "Select Instance",
                MobileLabel: "Types",
                showAllOption: true,
                searchEnabled: false
            }

        ];
    };

    // Determine if we should show full UI
    const showFullUI = shouldShowFullUI(ctrc, selectedFilters);
    const filterOptions = getFilterOptions(showFullUI, contractTypeOptions, connData, selectedFilters, tempFilters);

    // mobile filters icon click button
    const handleFilterIconClick = (key) => {
        setActiveTab(key);
        clickedTabRef.current = true;
        setTimeout(() => {
            clickedTabRef.current = false;
        }, 500);
        const el = document.getElementById(key);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    // Filter headers based on hideTypeColumn state
    const getFilteredHeaders = () => {
        return tableHeaderCols().filter(header => {
            if (hideTypeColumn && header.key === 'bizType') {
                return false;
            } else if (header.key === 'instance' && selectedFilters.status === 1) {
                return false;
            } else if (header.key === 'status' && selectedFilters.status === 2) {
                return false;
            }
            return true;
        });
    };

    useEffect(() => {
        if (!isMobile) {
            setIsFilterSelected(true)
        }
    }, [])

    //connection data for filter
    useEffect(() => {
        if (!Array.isArray(permissionFilteredConnections)) {
            setConndata([]);
            return;
        }
        const processed = permissionFilteredConnections
            .filter(item => !(item.IsCold === 1 || item.IsCold === true))
            .map(item => ({
                connID: item.ConnID,
                bizName: item.BizName || "",
                contactName: item.ContactName || ""
            }))
            .sort((a, b) => (a.bizName || "").localeCompare(b.bizName || ""));
        setConndata(processed);
    }, [permissionFilteredConnections]);
    const convertFilterToAPIParams = (selectedFilters) => {
        const {
            status,
            dateRange,
            otherMonth,
            otherQuarter,
            otherYear,
            ctrcType,
            expiryDT
        } = selectedFilters;

        // Determine TypeID based on status
        let typeID = 2; // Default to "Started in" 
        if (status === 1) {
            typeID = 1; // "Renewal expected in"
        }

        // Convert ctrcType filter
        let ctrcTypeID = 0; // 0 = All (as per SP requirement)
        if (ctrcType && ctrcType !== "all") {
            ctrcTypeID = parseInt(ctrcType);
        }

        // Convert date filters to SP parameters
        let prdType = "ALL";
        let prdQty = 1;
        let year = 0;
        let month = 0;

        const calculatePrdQty = (startDate, endDate, type) => {
            if (type === "MM") {
                const startMonth = startDate.getMonth();
                const startYear = startDate.getFullYear();
                const endMonth = endDate.getMonth();
                const endYear = endDate.getFullYear();
                
                const monthDiff = (endYear - startYear) * 12 + (endMonth - startMonth);
                return monthDiff > 0 ? 2 : 1;
            }
            return 1;
        };

        // Handle Active status filters (renewal-based)
        if (status === 1 && expiryDT && expiryDT !== "all") {
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear();
            const currentMonth = currentDate.getMonth() + 1; // 1-based month

            switch (expiryDT) {
                case "This Month":
                    prdType = "MM";
                    year = currentYear;
                    month = currentMonth;
                    prdQty = 1;
                    break;
                case "Next Month":
                    prdType = "MM";
                    year = currentYear;
                    month = currentMonth + 1;
                    prdQty = 1;
                    break;
                case "This Quarter":
                    prdType = `QQ${Math.ceil(currentMonth / 3)}`;
                    year = currentYear;
                    month = currentMonth;
                    prdQty = 1;
                    break;
                case "This Year":
                    prdType = "YY";
                    year = currentYear;
                    prdQty = 1;
                    break;
                case "This Week":
                    prdType = "MM";
                    const weekStart = new Date(currentDate);
                    weekStart.setDate(currentDate.getDate() - currentDate.getDay());
                    const weekEnd = new Date(weekStart);
                    weekEnd.setDate(weekStart.getDate() + 6);
                    year = weekStart.getFullYear();
                    month = weekStart.getMonth() + 1;
                    prdQty = calculatePrdQty(weekStart, weekEnd, "MM");
                    break;
                default:
                    prdQty = 1;
                    break;
            }
        }
        else if (status === 2 && dateRange && dateRange !== "all") {
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear();
            const currentMonth = currentDate.getMonth() + 1;

            switch (dateRange) {
                case "This Month":
                    prdType = "MM";
                    year = currentYear;
                    month = currentMonth;
                    prdQty = 1;
                    break;
                case "Last Month":
                    const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
                    const lastMonthYear = currentMonth === 1 ? currentYear - 1 : currentYear;
                    prdType = "MM";
                    year = lastMonthYear;
                    month = lastMonth;
                    prdQty = 1;
                    break;
                case "This Week":
                    // Week might span two months
                    prdType = "MM";
                    const weekStart = new Date(currentDate);
                    weekStart.setDate(currentDate.getDate() - currentDate.getDay());
                    const weekEnd = new Date(weekStart);
                    weekEnd.setDate(weekStart.getDate() + 6);
                    year = weekStart.getFullYear();
                    month = weekStart.getMonth() + 1;
                    prdQty = calculatePrdQty(weekStart, weekEnd, "MM");
                    break;
                case "This Quarter":
                    prdType = `QQ${Math.ceil(currentMonth / 3)}`;
                    year = currentYear;
                    month = currentMonth;
                    prdQty = 1;
                    break;
                case "This Year":
                    prdType = "YY";
                    year = currentYear;
                    prdQty = 1;
                    break;
                case "Other Month":
                    if (otherMonth && otherMonth !== "all") {
                        const [monthName, yearStr] = otherMonth.split(' ');
                        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                        prdType = "MM";
                        year = parseInt(yearStr);
                        month = monthNames.indexOf(monthName) + 1;
                        prdQty = 1;
                    }
                    break;
                case "Other Quarter":
                    if (otherQuarter && otherQuarter !== "all") {
                        const [quarterStr, yearStr] = otherQuarter.split(' ');
                        const quarterNum = quarterStr.replace('Q', '');
                        prdType = `QQ${quarterNum}`;
                        year = parseInt(yearStr);
                        // For quarters, we can set month to the first month of the quarter
                        month = (parseInt(quarterNum) - 1) * 3 + 1;
                        prdQty = 1;
                    }
                    break;
                case "Other Year":
                    if (otherYear && otherYear !== "all") {
                        prdType = "YY";
                        year = parseInt(otherYear);
                        prdQty = 1;
                    }
                    break;
                default:
                    prdQty = 1;
                    break;
            }
        } else {
            // For "ALL" filter or no specific filter, set prdQty to 1
            prdQty = 1;
        }

        return {
            typeID,
            ctrcTypeID,
            prdType,
            prdQty,
            year,
            month
        };
    };

    // //API for Ctrc data
    // useEffect(() => {
    //     fetchCtrcData();
    // }, []);

    //Object making
    const getContractsObj = (contracts) => {
        return contracts.map((c) => {
            const conn = permissionFilteredConnections.find((x) => x.ConnID === c.ConnID) || {};

            return {
                contractID: c.CtrcID,
                contractNo: c.CtrcNo,
                product: c.Product,
                notes: c.Notes,
                contractType: c.CtrcType,
                statusID: c.StatusID,
                startDT: c.StartDT,
                expiryDT: c.ExpiryDT,
                contractCharges: c.CtrcCharges,
                renewalCharges: c.RenwCharges,
                currencyID: c.CurrencyID,
                attachment: c.CtrcFile,
                currentVersion: c.CurrentVersion,
                isRenewed: c.IsRenwed,
                renewedDT: c.RenwDT,
                cancelledDT: c.CancelledDT,
                cancelledNotes: c.CancelledNotes,
                isSeller: c.IsSeller,
                // buyer info
                buyerAgencyID: c.BuyerAgencyID,
                buyerAgencyName: c.BuyerAgencyName || "",
                // mapped connection data
                connID: c.ConnID,
                bizName: conn.BizName || "",
                mobile: conn.Mobile || "",
                email: conn.Email || "",
                connType: conn.ConnType || "",
                contactName: conn.ContactName || "",
                oldContractID: c.OldContractID,
                taskIDs: c?.TaskIDs || ""
            };
        });
    };

    useEffect(() => {
        if (isFilterSelected && isMobile) {
            setIsDrawerOpen(true);
            setTempFilters(selectedFilters);
        }
    }, [isFilterSelected, isMobile]);

    // Apply filters
    // useEffect(() => {
    //     if (ctrc.length > 0) {
    //         setIsLoading(true);
    //         applyFilters();
    //         setIsLoading(false);
    //     }
    // }, [ctrc, selectedFilters]);

    //managing paramConnID params
    useEffect(() => {
        const conn = searchParams.get("cid");
        if (conn && conn !== paramConnID) {
            setParamConnID(conn);
        }
    }, [location.search]);

    useEffect(() => {
        if (paramConnID !== '') {
            setSelectedFilters(prev => ({
                ...prev,
                connID: paramConnID,
            }));
            setTempFilters(prev => ({
                ...prev,
                connID: paramConnID,
            }));
            if (!isMobile) {
                setIsFilterSelected(true);
            }
        }
    }, [paramConnID, isMobile]);

    //Reset Sort Function
    const resetSort = () => {
        setSortConfig({ key: null, direction: null });
    };

    //Sorting function
    const sortData = (key, dir) => {
        if (!key) return;

        const actualKey = sortKeyMap[key] || key;

        // Determine direction
        let direction =
            dir ??
            (sortConfig.key === actualKey && sortConfig.direction === "asc" ? "desc" : "asc");

        setSortConfig({ key: actualKey, direction });

        const sorted = [...filteredCtrc].sort((a, b) => {
            let aVal = getNestedValue(a, actualKey);
            let bVal = getNestedValue(b, actualKey);

            if (["contractCharges", "renewalCharges"].includes(actualKey)) {
                aVal = Number(aVal) || 0;
                bVal = Number(bVal) || 0;
                return direction === "asc" ? aVal - bVal : bVal - aVal;
            }

            if (["startDT", "expiryDT"].includes(actualKey)) {
                const aDateValid = aVal && !isNaN(new Date(aVal).getTime());
                const bDateValid = bVal && !isNaN(new Date(bVal).getTime());

                if (!aDateValid && !bDateValid) return 0;
                if (!aDateValid) return 1;
                if (!bDateValid) return -1;

                aVal = new Date(aVal).getTime();
                bVal = new Date(bVal).getTime();
                return direction === "asc" ? aVal - bVal : bVal - aVal;
            }

            // ---- String fields (default) ----
            aVal = aVal?.toString().toLowerCase() || "";
            bVal = bVal?.toString().toLowerCase() || "";
            return direction === "asc"
                ? aVal.localeCompare(bVal)
                : bVal.localeCompare(aVal);
        });
        setFilteredCtrc(sorted);
        setPreSearchCtrc(sorted);
    };

    //Serch Filter
    useEffect(() => {
        const term = searchTerm.trim().toLowerCase();

        if (!term) {
            setFilteredCtrc(preSearchCtrc);
            return;
        }

        const filtered = preSearchCtrc.filter((ctrc) => {
            const fieldsToSearch = [
                ctrc.contractNo,
                ctrc.product,
                ctrc.contractType,
                ctrc.expiryDT,
                ctrc.startDT,
                ctrc.contractCharges?.toString(),
                ctrc.renewalCharges?.toString(),
                ctrc.bizName,
                ctrc.mobile,
                ctrc.email
            ];

            return fieldsToSearch
                .filter(Boolean)
                .some((field) =>
                    field.toString().toLowerCase().includes(term)
                );
        });

        setFilteredCtrc(filtered);
        setCurrentPage(1);
    }, [searchTerm, preSearchCtrc]);

    const needsAPICall = (selectedFilters, prevFilters) => {
        const apiDependentFilters = ['status', 'dateRange', 'otherMonth', 'otherQuarter', 'otherYear', 'expiryDT'];

        return apiDependentFilters.some(filter =>
            selectedFilters[filter] !== prevFilters[filter]
        );
    };

    //Apply Filters Function
    const applyFilters = async (forceApiCall = false) => {
        setIsLoading(true);

        try {
            const prev = prevFiltersRef.current;
            const shouldCallAPI =
                forceApiCall ||
                !prev ||
                Object.keys(prev).length === 0 ||
                needsAPICall(selectedFilters, prev);

            let baseData = ctrc; // Use existing ctrc data by default

            // Only make API call if date/status related filters changed
            if (shouldCallAPI) {
                try {
                    const apiParams = convertFilterToAPIParams(selectedFilters);
                    const result = await getContractAPI(apiParams);

                    if (result && Array.isArray(result)) {
                        const processedCtrc = getContractsObj(result);
                        // Update the main ctrc state with new API data
                        setCtrc(processedCtrc);
                        baseData = processedCtrc;
                    } else {
                        console.warn('API returned unexpected data format:', result);
                        // Use existing data if API response is invalid
                        baseData = ctrc;
                    }
                } catch (apiError) {
                    // toast.error(apiError.message);
                    // Use existing data as fallback
                    baseData = ctrc;
                }
            }

            // Apply client-side filters that aren't handled by the API
            const {
                connID,
                cType,
                ctrcType,
                instance
            } = selectedFilters;

            let filtered = Array.isArray(baseData) ? [...baseData] : [];

            // First, filter contracts based on user's connection permissions
            // Only show contracts for connections the user has permission to view
            const allowedConnIDs = new Set(permissionFilteredConnections.map(conn => conn.ConnID));
            filtered = filtered.filter(contract => allowedConnIDs.has(contract.connID));

            // Apply connection filter
            if (connID && connID !== "all") {
                filtered = filtered.filter(ctrc =>
                    ctrc.connID && ctrc.connID.toString() === connID.toString()
                );
            }

            // Apply customer/supplier filter
            if (cType && cType !== "all") {
                filtered = filtered.filter(ctrc => {
                    if (cType === "Customer") return ctrc.isSeller === 1;
                    if (cType === "Supplier") return ctrc.isSeller === 0;
                    return true;
                });
            }

            if (ctrcType && ctrcType !== "all") {
                filtered = filtered.filter(ctrc => {
                    const contractValue = contractTypeOptions.find(
                        (c) => Number(c.ID) === Number(ctrcType)
                    )?.value;
                    return ctrc.contractType === contractValue;
                });
            }

            // Update column visibility based on filters
            if (cType && cType !== "all") {
                setHideTypeColumn(true);
            } else {
                setHideTypeColumn(false);
            }

            if (instance && instance !== "all") {
                filtered = filtered.filter(ctrc => {
                    if (instance === "new") return ctrc.oldContractID === 0;
                    if (instance === "renewal") return ctrc.oldContractID > 0;
                    return true;
                });
            }

            setFilteredCtrc(filtered);
            setPreSearchCtrc(filtered);
            setCurrentPage(1);

            // Update previous filters reference
            prevFiltersRef.current = { ...selectedFilters };

        } catch (error) {
            console.error('Error in filter processing:', error);
            // Don't show error toast for processing errors, only for API errors
            // Keep existing data
            if (!filteredCtrc.length && ctrc.length) {
                setFilteredCtrc(ctrc);
                setPreSearchCtrc(ctrc);
            }
        } finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        // Only apply filters if we have initial data and filters are actually set
        if (selectedFilters && Object.keys(selectedFilters).length > 0) {
            applyFilters();
        }
    }, [selectedFilters]);
    //handle actions Function
    const handleItemAction = (action, contract, type, event) => {
        event?.preventDefault();
        event?.stopPropagation();

        switch (action) {
            case "SELECT":
                if (type === "contract") {
                    setSelectedContractForDetail(contract);
                    setDialogStates((prev) => ({ ...prev, contractDetails: true }));
                }
                break;
            case "EDIT":
                if (type === "contract") {
                    const startDT = contract.startDT ? new Date(contract.startDT) : null;
                    const expireDT = contract.expiryDT ? new Date(contract.expiryDT) : null;

                    const prefilledData = {
                        connection: contract.connID,
                        contactPerson: contract.contactName,
                        contractID: contract.contractID,
                        contractNo: contract.contractNo,
                        contractType: contract.contractType,
                        product: contract.product,
                        startDate: startDT ? format(startDT, "yyyy-MM-dd") : "",
                        expireDate: expireDT ? format(expireDT, "yyyy-MM-dd") : "",
                        charges: contract.contractCharges,
                        renewal: contract.renewalCharges,
                        notes: contract.notes,
                        bizName: contract.bizName || "",
                        contactName: contract.contactName,
                        fileName: contract.attachment || "",
                        contractTypeSelection: contract.isSeller === 1 ? 'sold' : 'purchase'
                    };
                    setSelectedContract(contract);
                    setContractPrefilledData(prefilledData);
                    setDialogStates((prev) => ({ ...prev, contractEntry: true }));
                }
                break;

            case "DOWNLOAD":
                if (contract.attachment) {
                    const fileUrl = `/Uploads/${contract.attachment}`;
                    window.open(fileUrl, "_blank");
                }
                break;

            default:
                break;
        }
    };

    // Pagination
    const totalPages = Math.ceil(filteredCtrc.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    // Current Ctrc
    useEffect(() => {
        let sorted = [];

        if (!sortConfig.key) {
            sorted = [...filteredCtrc].sort((a, b) => {
                if (selectedFilters.status === "Active") {
                    setTempsortConfig({ key: "expiryDT", direction: "asc" });
                    const aDate = a.expiryDT ? new Date(a.expiryDT).getTime() : 0;
                    const bDate = b.expiryDT ? new Date(b.expiryDT).getTime() : 0;
                    return aDate - bDate; // ascending expiryDT
                } else {
                    setTempsortConfig({ key: "startDT", direction: "desc" });
                    const aDate = a.startDT ? new Date(a.startDT).getTime() : 0;
                    const bDate = b.startDT ? new Date(b.startDT).getTime() : 0;
                    return bDate - aDate; // descending startDT
                }
            });
        } else {
            sorted = [...filteredCtrc]; // if custom sort applied
        }
        if (!isMobile) {
            const sliced = sorted.slice(startIndex, endIndex);
            setCurrentCtrc(sliced.length > 0 ? sliced : sorted);
        } else {
            setCurrentCtrc(sorted);
        }
    }, [filteredCtrc, sortConfig, currentPage, rowsPerPage, isMobile]);

    // tan stack virtualizer for rendering cards in mobile efficiently
    const rowVirtualizer = useVirtualizer({
        count: currentCtrc.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 140,
        overscan: 5,
    });

    //Drawerfilter Cancel Function
    const handleCancel = () => {
        setTempFilters(selectedFilters);
        setIsFilterSelected(false);
        setIsDrawerOpen(false);
        // setTempsortConfig(sortConfig);
    };

    //Reset Filters Button
    const handleResetFilters = () => {
        const cleared = {
            connID: "all",
            dateRange: "all",
            otherMonth: "all",
            startDT: "all",         // New: Start Date filter
            expiryDT: "all",        // New: Expiry Date filter
            status: 1,          // New: Active/Inactive filter
            cType: "all",            // New: Customer/Supplier filter
            ctrcType: "all",
            otherQuarter: "all",
            otherYear: "all"
        };
        resetSort();
        setSelectedFilters(cleared);
        setSearchTerm("");
        setIsDrawerOpen(false);
        setTempFilters(cleared);
        setTempsortConfig({ key: null, direction: null });
        setSearchTerm("");
        setSortConfig({ key: null, direction: null });
    };

    useEffect(() => {
        if (isMobile) {
            setTempsortConfig({
                key: tempFilters.status === "Active" ? "expiryDT" : "startDT",
                direction: tempFilters.status === "Active" ? "asc" : "desc",
            });
        }
    }, [tempFilters.status, isMobile]);

    const MobileCard = ({ ctrc }) => {
        return (
            <div
                className="bg-sahi-white sahi-radius shadow-sm border border-gray-200 mb-2 w-full f-col justify-between cursor-pointer"
                onClick={() => handleItemAction("SELECT", ctrc, "contract")}
            >
                {/* Header Section */}
                <div className="f-col gap-1 mobile-tile-header-layout">
                    <div className="f-row justify-between gap-2">
                        {/* Left: Biz Info */}
                        <div className="f-col justify-between min-w-0 flex-1">
                            <div className="f-col min-w-0 gap-1">
                                <div className="flex items-center gap-2 mb-1 min-w-0">
                                    <Building2 size={14} />
                                    <span
                                        className={`${isMobile ? "text-xs" : "text-sm"} font-semibold truncate`}
                                    >
                                        {ctrc.bizName || "BizName"}
                                    </span>
                                </div>

                                {/* product: allow 2 lines ellipsis */}
                                <p className="text-sub-text-color text-xs truncate">
                                    {ctrc.product}
                                </p>

                                {ctrc.contractNo && (
                                    <p className="flex items-center text-xs text-sub-text-color mt-1 gap-1 truncate">
                                        <div>{getContractIcon(ctrc.isSeller)}</div>
                                        #<strong>{ctrc.contractNo}</strong>
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Right: Charges */}
                        <div className="f-col items-end text-right gap-1 flex-shrink-0">
                            <div className="text-sub-text-color text-xs truncate">
                                {ctrc?.contractType}
                            </div>

                            <span className="text-sm font-medium text-color mb-1 p-0">
                                <FormatAmount
                                    wholeCss="!flex-row  !justify-center !text-center"
                                    amount={ctrc.contractCharges}
                                    currency={getCurrencySymbol(ctrc.currencyID)}
                                />
                            </span>

                            <div className="flex items-center gap-1 self-end">
                                <Recycle className="h-3 w-3 text-sahi-blue" />
                                <span className="font-medium text-color !text-sub-text-color !text-xs">
                                    <FormatAmount
                                        amount={ctrc.renewalCharges}
                                        currency={getCurrencySymbol(ctrc.currencyID)}
                                    />
                                </span>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Footer Section */}
                <div className="flex justify-between items-center text-xs border-t border-[#f1f1f1] w-full mobile-tile-footer-layout">
                    <div>
                        {ctrc.expiryDT && (
                            <div className={`flex items-center gap-1 text-xs text-sub-text-color mt-0.5`}>
                                <HourglassIcon className="h-3 w-3 text-sahi-red" />
                                <strong>{getTableDateOrWeekday(ctrc.expiryDT)}</strong>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-end gap-2 xl:gap-2.5">
                        {ctrc.attachment && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className={`!px-0 !mx-0 ${actionButton}`}
                                        onClick={(e) =>
                                            handleItemAction("DOWNLOAD", ctrc, "contract", e)
                                        }
                                    >
                                        <Paperclip className="h-3 w-3 text-sahi-blue" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Attachment</p>
                                </TooltipContent>
                            </Tooltip>
                        )}

                        <ContactActions
                            connectionData={{
                                mobile: ctrc.mobile,
                                email: ctrc.email,
                                cc: ctrc.connType,
                            }}
                            showEmail={false}
                        />
                        {canEditContracts &&
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className={`!px-0 !mx-0 ${actionButton}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleItemAction("EDIT", ctrc, "contract", e);
                                        }}                                >
                                        <Pencil className="h-3 w-3 text-sahi-blue pr-0 mr-0" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Edit Contract</p>
                                </TooltipContent>
                            </Tooltip>
                        }

                    </div>
                </div>
            </div>
        );
    };


    // Table For Web view
    const WebDataTable = () => {
        return (
            <>
                {isLoading ?
                    (null) :
                    (
                        currentCtrc.length === 0 ? (
                            <>
                                <div className="f-col items-start gap-5 mt-0">
                                    <div className="no-records border border-gray-200 shadow-sm sahi-radius">
                                        No Contracts found.
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <ContractDropdown
                                            onContractTypeSelect={handleContractTypeSelection}
                                            externalOpen={contractDropdownOpen2}
                                            onExternalOpenChange={setContractDropdownOpen2}
                                            className="text-sm"
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <Card className="bg-sahi-white hidden lg:block !pt-0 overflow-hidden ">
                                    <CardContent className="px--5 !sahi-radius">
                                        <Table className="min-w-full table-auto">
                                            <TableHeader>
                                                <TableRow className="bg-sahi-white py-1.5 h-11" >
                                                    {getFilteredHeaders().map(
                                                        ({ key, label, className = "", style = {} }) => (
                                                            <TableHead
                                                                key={key}
                                                                className={`table-header-text word-break-px ${className}`}
                                                                style={style}
                                                                onClick={() => sortData(key)}
                                                            >
                                                                <div className="flex items-center cursor-pointer break-words">
                                                                    {label}
                                                                    {!["bizType"].includes(key) &&
                                                                        sortConfig.key === key &&
                                                                        sortConfig.direction && (
                                                                            sortConfig.direction === "asc" ? (
                                                                                <ArrowUp className="ml-1 h-4 w-4 text-black" />
                                                                            ) : (
                                                                                <ArrowDown className="ml-1 h-4 w-4 text-black" />
                                                                            )
                                                                        )}
                                                                </div>
                                                            </TableHead>
                                                        )
                                                    )}
                                                    <TableHead className="table-header-text whitespace-nowrap text-left pr-2 align-top w-32">
                                                    </TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {currentCtrc.map((ctr) => (
                                                    <TableRow
                                                        key={ctr.contractID}
                                                        className="hover:bg-gray-50 cursor-pointer"
                                                        onClick={() => handleItemAction("SELECT", ctr, "contract")}
                                                    >
                                                        {!hideTypeColumn && (
                                                            <TableCell className="w-[3vw] px-4 text-center">
                                                                <div className="flex">
                                                                    {getContractIcon(ctr.isSeller)}
                                                                </div>
                                                            </TableCell>
                                                        )}

                                                        {selectedFilters.connID === "all" &&
                                                            (<TableCell className="word-non-break-px pl-2">
                                                                {ctr.bizName || "-"}
                                                            </TableCell>)
                                                        }

                                                        <TableCell className="table-cell">
                                                            {ctr.contractNo || "-"}
                                                        </TableCell>

                                                        {selectedFilters.ctrcType === "all" &&
                                                            (<TableCell className="table-cell">
                                                                {ctr.contractType || "-"}
                                                            </TableCell>)}

                                                        {selectedFilters.status !== 1 && <TableCell className="table-cell">
                                                            {ctr.oldContractID ? "Renewal" : "New"}
                                                        </TableCell>}

                                                        <TableCell className="table-cell">
                                                            {ctr.product || "-"}
                                                        </TableCell>

                                                        <TableCell className="table-cell-amt">
                                                            <FormatAmount
                                                                showCurr={false}
                                                                amount={ctr.contractCharges}
                                                                currency={getCurrencySymbol(ctr.currencyID)}
                                                            />
                                                        </TableCell>

                                                        <TableCell className="table-cell">
                                                            {getTableDateOrWeekday(ctr.startDT) || "-"}
                                                        </TableCell>

                                                        <TableCell className="table-cell-amt">
                                                            <FormatAmount
                                                                showCurr={false}
                                                                amount={ctr.renewalCharges}
                                                                currency={getCurrencySymbol(ctr.currencyID)}
                                                            />
                                                        </TableCell>

                                                        <TableCell className="table-cell">
                                                            <div className={`text-sm ${ctr.isRenewed ? '!text-sahi-blue' : ''} ${getColorClass(getTaskColor(ctr.expiryDT))} whitespace-nowrap`}>
                                                                {ctr.expiryDT ? getTableDateOrWeekday(ctr.expiryDT) : "--"}
                                                            </div>
                                                        </TableCell>

                                                        {selectedFilters.status !== 2 && <TableCell className="table-cell">
                                                            {ctr.isRenewed ? "Renewed" : "-"}
                                                        </TableCell>}

                                                        <TableCell className={`text-right`}>
                                                            <div className="flex items-center justify-end gap-2 xl:gap-2.5 pr-2">
                                                                {ctr.attachment && (
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                className={`!px-0 !mx-0 ${actionButton}`}

                                                                                onClick={(e) => handleItemAction("DOWNLOAD", ctr, "contract", e)}
                                                                            >
                                                                                <Paperclip className="h-3 w-3 text-sahi-blue" />
                                                                            </Button>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            <p>Attachment</p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                )}

                                                                <ContactActions
                                                                    connectionData={{
                                                                        mobile: ctr.mobile,
                                                                        email: ctr.email,
                                                                        cc: ctr.connType
                                                                    }}
                                                                    showEmail={false}
                                                                />
                                                                {canEditContracts &&
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                className={`!px-0 !mx-0 ${actionButton}`}

                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                    handleItemAction("EDIT", ctr, "contract", e);
                                                                                }}>
                                                                                <Pencil className="h-3 w-3 text-sahi-blue" />
                                                                            </Button>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            <p>Edit Contract</p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                }
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}

                                            </TableBody>
                                        </Table>
                                    </CardContent>
                                </Card>

                                {/* Pagination Component */}
                                <PaginationComp
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={(size) => setRowsPerPage(size)}
                                    onPageChange={(page) => setCurrentPage(page)}
                                />
                            </>
                        )
                    )}
            </>
        );
    };

    const SummaryBox = ({
        items,
        filteredCtrc,
        getCurrencySymbol,
        showAllOption = false
    }) => {
        const currencySymbol = getCurrencySymbol();

        // Calculate values without useMemo
        const values = items.map((item) => {
            const total = filteredCtrc.reduce((sum, c) => sum + (c[item.field] || 0), 0);
            return { label: item.label, value: total };
        });

        // Add "All" summary if showAllOption is true
        if (showAllOption) {
            const allTotal = filteredCtrc.reduce((sum, c) => {
                return sum + items.reduce((itemSum, item) => itemSum + (c[item.field] || 0), 0);
            }, 0);
            values.unshift({ label: "All", value: allTotal });
        }

        if (!values.length) return null;

        return (
            <div className="flex flex-wrap items-center justify-start lg:justify-end gap-2 lg:gap-3 w-full">
                {values.map((val, ID) => (
                    val.value !== 0 && (<Button
                        key={ID}
                        variant="noHoversemidefault"
                        className={cn(
                            "flex items-center px-3 lg:px-4 h-8 lg:h-10",
                            "w-fit max-w-[240px] bg-white text-black rounded-lg border filter-border shadow-sm"
                        )}
                    >
                        <span className="f-row gap-1 whitespace-nowrap text-xs lg:text-sm">
                            <span>{val.label}: </span>
                            <FormatAmount amount={val.value} currency={currencySymbol} />
                        </span>
                    </Button>)
                ))}
            </div>
        );
    };

    const FilterButton = () => {
        let activeFilterClass = isFilterSelected && !isMobile ? "filter-active" : "";
        let filterIconToggleClass = "h-4 w-4 " + (isMobile ? "" : (isFilterSelected ? "text-sahi-primary-color" : "text-white group-hover:text-sahi-primary-color"));
        return (
            <Button
                className={cn(filterBtn, activeFilterClass)}
                onClick={() => setIsFilterSelected(true)}
                aria-label="Toggle Filter"
            >
                <div className="h-6 w-6 flex items-center justify-center transition-all">
                    <Filter className={cn(filterIconToggleClass)} />
                </div>
            </Button>
        )
    };

    return (
        <TooltipProvider>
            <div className="space-y-3 lg:space-y-6 overflow-x-hidden">
                {/*--------------- Header ------------*/}
                <div className="hidden lg:flex justify-between items-center gap-3">
                    <div className="flex items-center justify-between flex-grow">
                        {/* Left Side: Title */}
                        <h1 className="text-3xl font-bold text-text-color web-header-name">
                            Contracts
                        </h1>

                        <div className="flex items-center gap-3">
                            <SummaryBox
                                filteredCtrc={filteredCtrc}
                                getCurrencySymbol={getCurrencySymbol}
                                items={[
                                    { label: "Total Charges", field: "contractCharges" },
                                    { label: "Total Renewals", field: "renewalCharges" },
                                ]}
                            />
                            <div className="relative w-120 filter-border">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dropdown-white" />
                                <Input
                                    placeholder="Search"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 h-10 border-none focus:outline-none focus:ring-0 shadow-none"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <ContractDropdown
                            onContractTypeSelect={handleContractTypeSelection}
                            externalOpen={contractDropdownOpen}
                            onExternalOpenChange={setContractDropdownOpen}
                            className="text-base"
                        />

                        <Button
                            className={cn(
                                "button-config sahi-primary-button flex items-center justify-center group px-2.5 py-[5px] ",
                                isFilterSelected && "filter-active"
                            )}
                            onClick={() => setIsFilterSelected(prev => !prev)}
                            aria-label="Toggle filter"
                        >
                            <div className="h-6 w-6 flex items-center justify-center transition-all">
                                <Filter
                                    className={cn(
                                        "h-4 w-4",
                                        isFilterSelected
                                            ? "text-sahi-primary-color"
                                            : "text-white group-hover:text-sahi-primary-color"
                                    )}
                                />
                            </div>
                        </Button>
                    </div>
                </div>

                {/* Mobile view */}
                <div className="f-col lg:hidden gap-3">
                    {/* Search and Filter Button */}
                    {ctrc.length > 0 && (
                        <div className="w-full flex items-center gap-2 pt-2">
                            {showFullUI && (
                                <div className="relative flex-grow">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dropdown-white" />
                                    <Input
                                        placeholder="Search"
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                        }}
                                        className="pl-10 h-10 w-full"
                                    />
                                </div>
                            )}
                            <FilterButton />
                        </div>
                    )}
                    {showFullUI && (
                        <SummaryBox
                            filteredCtrc={filteredCtrc}
                            getCurrencySymbol={getCurrencySymbol}
                            items={[
                                { label: "Total Charges", field: "contractCharges" },
                                { label: "Total Renewals", field: "renewalCharges" },
                            ]}
                        />
                    )}
                </div>

                {/*--------------- Filter ------------*/}
                {isFilterSelected &&
                    (isMobile ? (
                        <Drawer open={isDrawerOpen} onOpenChange={() => {
                            setIsDrawerOpen(false);
                            setIsFilterSelected(false);
                        }}>
                            <DrawerContent className="p-0 [&>div:first-child]:hidden">
                                <div className="f-col h-[100dvh]">
                                    {/* Header */}
                                    <div className="flex items-center justify-between p-4 border-b-2 border-gray-200 text-xl">
                                        <div className="flex gap-4 items-center ">
											<DrawerClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
												<ArrowLeft className="h-[24px] w-[24px]" onClick={handleCancel} />
												<span className="sr-only">Back</span>
											</DrawerClose>
											<p className="text-lg font-semibold text-sahi-primary-color">Contract Filters</p>
										</div>
										
                                        <button
                                            className="group flex items-center gap-1 text-sm font-light hover:underline transition duration-200 text-sahi-primary-color"
                                            onClick={() => {
                                                handleResetFilters();
                                                setIsFilterSelected(false);
                                            }}
                                        >
                                            <RiResetLeftFill className="group-hover:rotate-[-20deg] transition-transform duration-200" />
                                            <span>Reset</span>
                                        </button>
                                    </div>
                                    {/* Main Content */}
                                    <div className="flex flex-1 overflow-y-auto">
                                        {/* Scrollable Filters */}
                                        <div className="flex-1 p-3 overflow-y-auto filter-scroll-container">
                                            {[
                                                ...filterOptions.slice(0),
                                                {
                                                    key: "sort",
                                                    label: "Sort by",
                                                    options: Object.keys(sortkeyMapMobileFilter),
                                                    type: "sort",
                                                    className: "",
                                                }
                                            ].map(({ key, label, options, displayValue, type, mobileDropdownText, mobileNoLabel, MobileLabel, showAllOption, sortDescending, isSorting, mobiledisabled }) => (
                                                <div key={key} id={key} className="mb-4">
                                                    <DrawerFilter
                                                        key={key}
                                                        id={key}
                                                        label={label}
                                                        options={options}
                                                        value={tempFilters[key]}
                                                        displayValue={displayValue}
                                                        onChange={(value) => {
                                                            setTempFilters((prev) => ({
                                                                ...prev,
                                                                [key]: value,
                                                            }));
                                                        }}
                                                        disabled={mobiledisabled}
                                                        sortConfig={sortConfig}
                                                        setTempsortConfig={setTempsortConfig}
                                                        tempsortConfig={tempsortConfig}
                                                        sortOptionMap={sortkeyMapMobileFilter}
                                                        type={type}
                                                        mobileDropdownText={mobileDropdownText}
                                                        mobileNoLabel={mobileNoLabel}
                                                        MobileLabel={MobileLabel}
                                                        showAllOption={showAllOption}
                                                        sortDescending={sortDescending}
                                                        isSorting={isSorting}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    {/* Footer */}
                                    <DrawerFooter className="sticky bottom-0 border-t-2 p-3 bg-sahi-white">
                                        <DrawerClose>
                                            <div className="f-row justify-start gap-2">
                                                <Button
                                                    className="sahi-primary-button"
                                                    onClick={() => {
                                                        let filtersToApply = { ...tempFilters };

                                                        if (tempFilters.status === 1) {
                                                            filtersToApply = {
                                                                ...filtersToApply,
                                                                dateRange: "all",
                                                                otherMonth: "all",
                                                                otherQuarter: "all",
                                                                otherYear: "all"
                                                            };
                                                        } else {
                                                            filtersToApply = {
                                                                ...filtersToApply,
                                                                expiryDT: "all",
                                                            };
                                                        }
                                                        setTempFilters(filtersToApply);
                                                        setSelectedFilters(filtersToApply);
                                                        setIsDrawerOpen(false);
                                                        setIsFilterSelected(false);
                                                    }}
                                                >
                                                    Apply
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    className="sahi-secondary-button"
                                                    onClick={handleCancel}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </DrawerClose>
                                    </DrawerFooter>
                                </div>
                            </DrawerContent>
                        </Drawer>
                    ) : (
                        <div className="hidden lg:flex flex-wrap gap-4">
                            {filterOptions.map(({ key, label, options, displayValue, searchEnabled, disabled, showClearButton, hideAllOption }) => (
                                <CommonFilter
                                    key={key}
                                    label={label}
                                    options={options}
                                    value={selectedFilters[key]}
                                    onChange={(value) => {
                                        setSelectedFilters((prev) => ({ ...prev, [key]: value }));
                                        setTempFilters((prev) => ({ ...prev, [key]: value }));
                                    }}
                                    searchEnabled={searchEnabled}
                                    displayValue={displayValue}
                                    disabled={disabled}
                                    showClearButton={showClearButton}
                                    hideAllOption={hideAllOption}
                                />
                            ))}
                        </div>
                    ))}

                {/*--------------- Data Section ------------*/}
                {isMobile ? (
                    <div className="block lg:hidden">
                        <div ref={parentRef} className="overflow-auto scrollbar-hide threads-virtualized-scrollbar">
                            {currentCtrc.length > 0 ? (
                                <div
                                    className="scrollbar-hide overflow-y-hidden"
                                    style={{
                                        height: `${rowVirtualizer.getTotalSize()}px`,
                                        position: "relative",
                                    }}
                                >
                                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                                        const ctrc = currentCtrc[virtualRow.index];
                                        return (
                                            <div
                                                key={ctrc.contractID}
                                                ref={virtualRow.measureElement}
                                                className="absolute top-0 left-0 w-full"
                                                style={{
                                                    transform: `translateY(${virtualRow.start}px)`,
                                                }}
                                            >
                                                <MobileCard ctrc={ctrc} />
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <>
                                    <p className="no-records mb-3 lg:whitespace-nowrap">
                                        No Contracts found.
                                    </p>
                                </>
                            )}
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 pt-2">

                            {/* Add Button as Card */}
                            {currentCtrc.length < 10 && (
                                <ContractDropdown
                                    onContractTypeSelect={handleContractTypeSelection}
                                    externalOpen={contractDropdownOpen}
                                    onExternalOpenChange={setContractDropdownOpen}
                                    className="text-sm"

                                />
                            )}
                        </div>


                    </div>


                ) : (
                    <WebDataTable />
                )}
            </div>
            <ContractEntry
                isOpen={dialogStates.contractEntry}
                onClose={() => {
                    setDialogStates(prev => ({ ...prev, contractEntry: false }));
                    setSelectedContract(null);
                    setContractPrefilledData({});
                }}
                connID={contractPrefilledData.connection}
                prefilledData={contractPrefilledData}
                companyName={
                    selectedContract
                        ? connData.find((c) => c.connID === contractPrefilledData?.connection)?.bizName || ""
                        : ""
                }
                onContractSaved={() => {
                    applyFilters(true);

                }}
                contractID={selectedContract?.contractID || null}
            />
            <ContractDetails
                isOpen={dialogStates.contractDetails}
                onClose={() => {
                    setDialogStates(prev => ({ ...prev, contractDetails: false }));
                    setSelectedContractForDetail(null);
                }}
                contract={selectedContractForDetail ? {
                    ContractID: selectedContractForDetail.contractID,
                    ContractNo: selectedContractForDetail.contractNo,
                    ContractType: selectedContractForDetail.contractType,
                    Product: selectedContractForDetail.product,
                    StartDT: selectedContractForDetail.startDT,
                    ExpiryDT: selectedContractForDetail.expiryDT,
                    ContractCharges: selectedContractForDetail.contractCharges,
                    RenewalCharges: selectedContractForDetail.renewalCharges,
                    Notes: selectedContractForDetail.notes,
                    Attachment: selectedContractForDetail.attachment,
                    ContactName: selectedContractForDetail.contactName,
                    IsSeller: selectedContractForDetail.isSeller,
                    CurrencyID: selectedContractForDetail.currencyID,
                    StatusID: selectedContractForDetail.statusID,
                    BizName: selectedContractForDetail.bizName,
                    ConnID: selectedContractForDetail.connID,
                    TaskIDs: selectedContractForDetail.taskIDs
                } : null}
                connection={connData.find(c => c.connID === selectedContractForDetail?.connID) ? {
                    ConnID: selectedContractForDetail?.connID,
                    BizName: selectedContractForDetail?.bizName,
                    ContactName: selectedContractForDetail?.contactName,
                    Mobile: selectedContractForDetail?.mobile,
                    Email: selectedContractForDetail?.email,
                    CC: selectedContractForDetail?.connType
                } : null}
                currencySymbol={currencySymbol}
                onContractUpdate={(updatedContract) => {
                    // Refresh the contracts data
                    applyFilters(true);
                }}
            />
        </TooltipProvider>
    );
};

export default Contracts;